import React from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Truck, CreditCard, Lock, PlayCircle, RotateCcw } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { useTutorial } from "@/context/TutorialContext";
import { useIsFeatureEnabled } from "@/hooks/useFeatureFlag";
import { Button } from "@/components/ui/button";

const ICONS = {
  "basic-dynicty": BookOpen,
  "jubelio-shipment": Truck,
  "midtrans-payment": CreditCard,
};

function StatusBadge({ status }) {
  if (status === "completed") {
    return (
      <span
        className="text-[11px] font-bold px-2 py-1 rounded-full bg-green-50 text-green-700 border border-green-200"
        data-testid="tutorial-status-completed"
      >
        Selesai
      </span>
    );
  }
  if (status === "in_progress") {
    return (
      <span
        className="text-[11px] font-bold px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
        data-testid="tutorial-status-in-progress"
      >
        Sedang Berjalan
      </span>
    );
  }
  return null;
}

function ActiveTutorialCard({ tutorial }) {
  const navigate = useNavigate();
  const { start, getProgress, resetTutorial } = useTutorial();
  const featureEnabled = useIsFeatureEnabled(tutorial.featureKey || null);
  const featureUnavailable = tutorial.featureKey && !featureEnabled;
  const progress = getProgress(tutorial.id);
  const status = progress?.status || "not_started";
  const stepIndex = progress?.stepIndex ?? -1;
  const totalSteps = tutorial.stepsCount || 1;
  const percent =
    status === "completed"
      ? 100
      : status === "in_progress" && stepIndex >= 0
      ? Math.round(((stepIndex + 1) / totalSteps) * 100)
      : 0;
  const Icon = ICONS[tutorial.id] || BookOpen;

  const primaryLabel =
    status === "in_progress"
      ? "Lanjutkan Panduan"
      : status === "completed"
      ? "Ulangi Panduan"
      : "Mulai Panduan";

  const handlePrimary = () => {
    // Jika tutorial punya guide page → arahkan ke sana (user baca dulu).
    // Kalau tidak punya, langsung mulai overlay dari Pusat Panduan.
    if (tutorial.guidePagePath) {
      navigate(tutorial.guidePagePath);
      return;
    }
    if (status === "in_progress") {
      start(tutorial.id, { resume: true });
    } else {
      // Fresh start (also for "completed" → replay from step 1).
      if (status === "completed") resetTutorial(tutorial.id);
      start(tutorial.id, { resume: false });
    }
  };

  return (
    <div
      className="bg-white rounded-2xl border border-[#E5E5E5] p-5 flex flex-col shadow-sm"
      data-testid={`tutorial-card-${tutorial.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#FFEDD5] text-[#E85D04] grid place-items-center shrink-0">
          <Icon size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-bold text-lg text-[#1A1A1A]">
              {tutorial.title}
            </h3>
            <StatusBadge status={status} />
          </div>
          <p className="text-sm text-[#5C5C5C] mt-1">{tutorial.description}</p>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3 text-xs text-[#5C5C5C]">
        <span data-testid={`tutorial-steps-count-${tutorial.id}`}>
          {tutorial.stepsCount} langkah
        </span>
        <span>·</span>
        <span>Sekitar {tutorial.estimatedMinutes} menit</span>
      </div>

      {/* Progress bar */}
      <div
        className="mt-3 h-2 bg-[#F1F1F1] rounded-full overflow-hidden"
        aria-label="Progress panduan"
      >
        <div
          className="h-full brand-btn-primary"
          style={{ width: `${percent}%`, transition: "width 200ms ease" }}
          data-testid={`tutorial-progress-${tutorial.id}`}
        />
      </div>
      <div className="mt-1 text-[11px] text-[#5C5C5C]">
        {status === "in_progress"
          ? `Langkah ${stepIndex + 1} dari ${totalSteps}`
          : status === "completed"
          ? "Panduan selesai"
          : "Belum dimulai"}
      </div>

      {featureUnavailable && (
        <div
          className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5"
          data-testid={`tutorial-feature-unavailable-${tutorial.id}`}
        >
          Fitur ini belum tersedia untuk toko Anda. Panduan tetap bisa Anda buka
          untuk dibaca.
        </div>
      )}

      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <Button
          onClick={handlePrimary}
          className="brand-btn-primary rounded-xl font-bold"
          data-testid={`tutorial-start-btn-${tutorial.id}`}
        >
          <PlayCircle size={16} className="mr-1" /> {primaryLabel}
        </Button>
        {status !== "not_started" && (
          <Button
            variant="outline"
            onClick={() => resetTutorial(tutorial.id)}
            className="rounded-xl"
            data-testid={`tutorial-reset-btn-${tutorial.id}`}
          >
            <RotateCcw size={14} className="mr-1" /> Ulangi dari Awal
          </Button>
        )}
      </div>
    </div>
  );
}

function ComingSoonCard({ tutorial }) {
  const Icon = ICONS[tutorial.id] || BookOpen;
  return (
    <div
      className="bg-white rounded-2xl border border-dashed border-[#E5E5E5] p-5 opacity-80 flex flex-col shadow-sm"
      data-testid={`tutorial-card-${tutorial.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#F1F1F1] text-[#5C5C5C] grid place-items-center shrink-0">
          <Icon size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-bold text-lg text-[#1A1A1A]">
              {tutorial.title}
            </h3>
            <span
              className="text-[11px] font-bold px-2 py-1 rounded-full bg-[#F1F1F1] text-[#5C5C5C] border border-[#E5E5E5]"
              data-testid={`tutorial-coming-soon-${tutorial.id}`}
            >
              Segera Hadir
            </span>
          </div>
          <p className="text-sm text-[#5C5C5C] mt-1">{tutorial.description}</p>
        </div>
      </div>

      <div className="mt-5">
        <Button
          disabled
          className="rounded-xl w-full sm:w-auto cursor-not-allowed"
          data-testid={`tutorial-locked-btn-${tutorial.id}`}
        >
          <Lock size={14} className="mr-1" /> Segera Hadir
        </Button>
      </div>
    </div>
  );
}

export default function TutorialCenter() {
  const { tutorials } = useTutorial();

  return (
    <AdminLayout title="Pusat Panduan">
      <div className="max-w-5xl space-y-6" data-testid="tutorial-center-page">
        <div>
          <h1 className="font-display text-3xl font-black text-[#1A1A1A]">
            Pusat Panduan
          </h1>
          <p className="text-sm text-[#5C5C5C] mt-1 max-w-2xl">
            Pilih panduan yang ingin Anda ikuti. Anda bisa berhenti kapan saja
            dan melanjutkannya di lain waktu — progress tersimpan otomatis.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {tutorials.map((t) =>
            t.status === "active" ? (
              <ActiveTutorialCard key={t.id} tutorial={t} />
            ) : (
              <ComingSoonCard key={t.id} tutorial={t} />
            )
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
